import React, { useState } from 'react';
import {
    Row, Col, Form, Label, Input, Card, CardBody,
    Container, Button
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useJsApiLoader } from "@react-google-maps/api";

const libraries = ["places"];

const GaragesCreate = () => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [address, setAddress] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
        libraries,
    });

    const [checkboxes, setCheckboxes] = useState({
        is_active: false,
    });

    const handleCheckboxChange = (key) => {
        setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSelect = async (value) => {
        setAddress(value);
        const results = await geocodeByAddress(value);
        const latLng = await getLatLng(results[0]);
        setLatitude(latLng.lat);
        setLongitude(latLng.lng);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title || !description || !address || !latitude || !longitude) {
            toast.error("All fields including location are required", { autoClose: 3000 });
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('address', address);
        formData.append('latitude', latitude);
        formData.append('longitude', longitude);
        formData.append('is_active', checkboxes.is_active);
        if (imageFile) formData.append('image', imageFile);

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/garages/create`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();

            if (response.status === 200) {
                toast.success('Garage created successfully', { autoClose: 3000 });
                setLoading(false);
                navigate(`/garages`);
            } else {
                setLoading(false);
                toast.error(data?.message || 'Error creating garage', { autoClose: 3000 });
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            toast.error("Failed to create garage", { autoClose: 3000 });
        }
    };

    if (!isLoaded) return <div>Loading Google Maps...</div>;

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs maintitle="Home" title="Garages" breadcrumbItem='Add Garage' />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            <div className="mb-3">
                                                <Label>Name</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter name"
                                                    value={title}
                                                    onChange={e => setTitle(e.target.value)}
                                                />
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            <div className="mb-3">
                                                <Label>Address</Label>
                                                <PlacesAutocomplete
                                                    value={address}
                                                    onChange={setAddress}
                                                    onSelect={handleSelect}
                                                >
                                                    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                                                        <div style={{ position: "relative" }}>
                                                            <Input
                                                                {...getInputProps({
                                                                    placeholder: "Search Address ...",
                                                                    className: "form-control",
                                                                })}
                                                            />
                                                            <div className="autocomplete-dropdown-container">
                                                                {loading && <div>Loading...</div>}
                                                                {suggestions.map((suggestion, idx) => {
                                                                    const style = {
                                                                        backgroundColor: suggestion.active ? "#fafafa" : "#ffffff",
                                                                        cursor: "pointer",
                                                                        padding: "5px 10px",
                                                                        borderBottom: "1px solid #eee"
                                                                    };
                                                                    return (
                                                                        <div key={idx} {...getSuggestionItemProps(suggestion, { style })}>
                                                                            {suggestion.description}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </PlacesAutocomplete>
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            <div className="mb-3">
                                                <Label>Image</Label>
                                                <Input type="file" accept="image/*" onChange={handleImageChange} />
                                                {imagePreview && (
                                                    <img src={imagePreview} alt="Preview" style={{ marginTop: 10, maxWidth: "100%", height: "auto" }} />
                                                )}
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            <div className="d-flex flex-wrap gap-2">
                                                {[
                                                    { label: "Is Active", key: "is_active" },
                                                ].map(({ label, key }) => (
                                                    <div
                                                        key={key}
                                                        className={`custom-box-checkbox ${checkboxes[key] ? "active" : ""
                                                            }`}
                                                        onClick={() => handleCheckboxChange(key)}
                                                    >
                                                        {label}
                                                    </div>
                                                ))}
                                            </div>
                                        </Col>

                                        <Col md="12">
                                            <div className="mb-3">
                                                <Label>Description</Label>
                                                <Input
                                                    type="textarea"
                                                    rows="5"
                                                    placeholder="Enter description"
                                                    value={description}
                                                    onChange={e => setDescription(e.target.value)}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col className="text-end">
                                            <Button type="submit" color="primary" disabled={loading}>
                                                {loading ? "Submitting..." : "Submit"}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default GaragesCreate;
